import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { BiFolder, BiPlus } from 'react-icons/bi'

import { FolderCategories } from '../../api/services/dietbase/types.ts'
import Accordion, { AccordionChildren } from '../../components/common/Accordion'
import TailButton from '../../components/common/TailButton/TailButton.tsx'
import useDietBaseFolders from '../../hooks/useDietBaseFolders.ts'
import Folders from './Folders.tsx'

const Categories = ({
  categories: _categories,
  isGlobal
}: {
  categories: FolderCategories[]
  isGlobal: boolean
}) => {
  const {
    createNewCategory,
    lastAddedCategory,
    setLastAddedCategory,
    editCategoryName,
    removeCategory
  } = useDietBaseFolders()

  const [categories, setCategories] = useState<FolderCategories[]>([])
  const [categorySelected, setCategorySelected] = useState<number | null>(null)
  const [newCategory, setNewCategory] = useState<string | null>(null)
  const [addNewCategory, setAddNewCategory] = useState(false)
  const [edited, setEdited] = useState<number | null>(null)

  const inputRef = useRef<HTMLInputElement | null>(null)

  useLayoutEffect(() => {
    setCategories(_categories)
  }, [_categories])

  useEffect(() => {
    if (lastAddedCategory) {
      setCategories((prevCategories) => {
        return [...prevCategories, lastAddedCategory]
      })
      setLastAddedCategory(null)
    }
  }, [lastAddedCategory])

  const editCategory = (category: string) => {
    setNewCategory(category)
  }

  const saveCategory = useCallback(() => {
    if (!newCategory) return

    if (edited === null && addNewCategory) {
      createNewCategory(newCategory as string)
    } else if (edited !== null && !addNewCategory) {
      editCategoryName(edited, newCategory as string)

      setCategories((prevCategories) => {
        const index = prevCategories.findIndex(
          (category) => category.id === edited
        )
        if (index !== -1) {
          const newCategories = [...prevCategories]
          newCategories[index].name = newCategory as string
          return newCategories
        }
        return prevCategories
      })
    }

    setAddNewCategory(false)
    setEdited(null)
  }, [newCategory])

  const prepareEditCategory = (categoryID: number, categoryName: string) => {
    setEdited(categoryID)
    setNewCategory(categoryName)

    setTimeout(() => {
      const input = inputRef.current
      if (!input) return

      input.focus()
      const len = input.value.length
      input.setSelectionRange(len, len)
    }, 0)
  }

  const removeCategoryHandler = async (categoryID: number) => {
    const isRemoved = await removeCategory(categoryID as number)
    if (!isRemoved) return

    setCategories((prevCategories) => {
      return prevCategories.filter((category) => category.id !== categoryID)
    })
  }

  const handleSelect = (props: AccordionChildren) => {
    props.setSqueezed(!props.squeezed)
    props.setLabel(props.newLabel)
  }

  return (
    <React.Fragment>
      <Accordion
        title={'Kategoria'}
        onSqueezeChange={(squeezed) => {
          if (!squeezed) setCategorySelected(null)
        }}
      >
        {({ squeezed, setSqueezed, setLabel, toggleSqueeze }) => (
          <div
            className={
              'flex items-center justify-center md:justify-start flex-wrap gap-4 mt-4'
            }
          >
            {categories &&
              categories.length > 0 &&
              categories.map((category, index) => (
                <div
                  key={category.id}
                  onClick={() =>
                    !addNewCategory && edited === null
                      ? handleSelect({
                          squeezed,
                          setSqueezed,
                          setLabel,
                          newLabel: category.name,
                          toggleSqueeze
                        })
                      : undefined
                  }
                >
                  <TailButton
                    key={category.id}
                    id={category.id}
                    title={
                      edited && category.id === edited ? '' : category.name
                    }
                    icon={
                      edited && category.id === edited ? undefined : (
                        <BiFolder
                          size={50}
                          color={
                            categorySelected === index ? '#16a34a' : '#bab9b9'
                          }
                        />
                      )
                    }
                    tailSelected={categorySelected}
                    setTailSelected={setCategorySelected}
                    disabled={
                      addNewCategory ||
                      (edited !== null && edited !== category.id)
                    }
                    onEdit={
                      !isGlobal && category.id !== edited && !addNewCategory
                        ? () => prepareEditCategory(category.id, category.name)
                        : undefined
                    }
                    onDelete={
                      !isGlobal && category.id !== edited && !addNewCategory
                        ? () => removeCategoryHandler(category.id)
                        : undefined
                    }
                    className={'w-50 bg-gray-50  dark:bg-gray-800 rounded'}
                  >
                    {edited === category.id && (
                      <div className={'flex flex-col gap-1 px-2'}>
                        <input
                          ref={inputRef}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => editCategory(e.target.value)}
                          value={newCategory ?? ''}
                          type="text"
                          placeholder="Nazwa kategorii"
                          className="border border-gray-300 rounded-md p-2 w-full"
                        />
                        <button
                          className="bg-green-500 text-white px-4 py-1 rounded-md hover:bg-green-600"
                          onClick={(e) => {
                            e.stopPropagation()
                            saveCategory()
                          }}
                        >
                          Zapisz
                        </button>
                        <button
                          className="bg-yellow-300 text-white px-4 py-1 rounded-md hover:bg-yellow-400"
                          onClick={(e) => {
                            e.stopPropagation()
                            setAddNewCategory(false)
                            setEdited(null)
                          }}
                        >
                          Anuluj
                        </button>
                      </div>
                    )}
                  </TailButton>
                </div>
              ))}

            {!isGlobal && (
              <TailButton
                key={9999}
                id={9999}
                title={'Dodaj nową kategorię'}
                icon={
                  !addNewCategory ? (
                    <BiPlus size={50} color={'#16a34a'} />
                  ) : undefined
                }
                externalFunction={() => setAddNewCategory(true)}
                disabled={edited !== null}
              >
                {addNewCategory && (
                  <div className={'flex flex-col gap-2'}>
                    <input
                      onChange={(e) => editCategory(e.target.value)}
                      type="text"
                      placeholder="Nazwa kategorii"
                      className="border border-gray-300 rounded-md p-2 w-full"
                    />
                    <div className={'flex flex-row justify-center gap-1'}>
                      <button
                        className="bg-yellow-300 text-white px-4 py-1 rounded-md hover:bg-yellow-400"
                        onClick={(e) => {
                          e.stopPropagation()
                          setAddNewCategory(false)
                          setEdited(null)
                        }}
                      >
                        Anuluj
                      </button>
                      <button
                        className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                        onClick={(e) => {
                          e.stopPropagation()
                          saveCategory()
                        }}
                      >
                        Zapisz
                      </button>
                    </div>
                  </div>
                )}
              </TailButton>
            )}
          </div>
        )}
      </Accordion>

      {categorySelected !== null && (
        <Folders isGlobal={isGlobal} categoryID={categorySelected as number} />
      )}
    </React.Fragment>
  )
}

export default React.memo(Categories)
